import { useEffect, useRef } from "react";

// Quiet period after the last selection change or fact edit before a
// regeneration fires. Long enough that several quick toggles batch into one
// run instead of cancelling and restarting the pipeline per click.
const EAGER_GENERATION_DEBOUNCE_MS = 2500;

interface UseEagerGenerationOptions {
  /** Number of dismissed source indices (triggers immediate generation when > 0) */
  dismissedSourceCount: number;
  /** Number of dismissed experience indices (triggers immediate generation when > 0) */
  dismissedExperienceCount?: number;
  /** True when a typed change (e.g. a to-do resolution edit) should fire a
   *  debounced regeneration. Agent-fact add/edit/remove/deselect do NOT use this
   *  — they are server-owned (the mutation schedules the regeneration). */
  factsChanged: boolean;
  /** Current message ID — generation is skipped when null/undefined */
  messageId: string | null | undefined;
  /** True while a generation mutation is already in flight */
  isGenerating: boolean;
  /** Called to trigger a (re)generation cycle */
  onGenerate: () => void;
  /**
   * Called synchronously the moment a dirty change is detected — i.e. when the
   * quiet-period debounce is (re)armed, before `onGenerate` fires. Lets callers
   * light the loading state immediately on the first modification instead of
   * waiting out the debounce. Safe to call repeatedly (e.g. wire to
   * `startRegen`, which is idempotent).
   */
  onDirty?: () => void;
}

/**
 * Eagerly fires answer regeneration when source selection changes,
 * without waiting for the user to click "Neu generieren".
 *
 * Both trigger kinds share one quiet-period debounce
 * (EAGER_GENERATION_DEBOUNCE_MS): source/experience toggles and
 * agent-facts edits each restart the timer, so a burst of quick changes
 * results in a single generation run after the user pauses.
 *
 * Used by both Neptune's ProposedAnswerTab and Venus's AgentPage so the
 * eager-generation UX is identical across surfaces.
 */
export function useEagerGeneration({
  dismissedSourceCount,
  dismissedExperienceCount = 0,
  factsChanged,
  messageId,
  isGenerating,
  onGenerate,
  onDirty,
}: UseEagerGenerationOptions): void {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Effect 1: debounced trigger on source/experience toggle changes —
  // each toggle restarts the quiet period so a burst of clicks becomes one
  // generation run. Callers must pass user-initiated toggle counts (not total
  // dismissed counts) so server-seeded initial state never fires a regen on
  // page load. Agent-fact actions are NOT eager-gen triggers: they are
  // server-owned (the mutation schedules the regeneration).
  useEffect(() => {
    const hasUserToggles =
      dismissedSourceCount > 0 || dismissedExperienceCount > 0;
    if (!hasUserToggles || !messageId) return;
    // Light the loading state now, on the first toggle, so the user gets
    // immediate feedback instead of waiting out the quiet period.
    onDirty?.();
    if (debounceRef.current != null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onGenerate();
    }, EAGER_GENERATION_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  // onGenerate/onDirty intentionally excluded — callers must memoize them with useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissedSourceCount, dismissedExperienceCount, messageId]);

  // Effect 2: debounced trigger for typed changes (factsChanged, e.g. a
  // to-do resolution edit). Skipped when toggle changes are present — Effect 1
  // already covers those.
  useEffect(() => {
    const hasToggleChanges =
      dismissedSourceCount > 0 || dismissedExperienceCount > 0;
    if (!factsChanged || !messageId || isGenerating || hasToggleChanges) {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }
    // Light the loading state now, on the first typed change, so the user gets
    // immediate feedback instead of waiting out the quiet period.
    onDirty?.();
    if (debounceRef.current != null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onGenerate();
    }, EAGER_GENERATION_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  // onGenerate/onDirty intentionally excluded — callers must memoize them with useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    factsChanged,
    dismissedSourceCount,
    dismissedExperienceCount,
    messageId,
    isGenerating,
  ]);
}
